import {
  Timestamp,
  Unsubscribe,
  deleteDoc,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  where,
} from "firebase/firestore";

import { FirestoreRefs } from "igfd-commons/src/firebase/refs";

import { newId } from "@dashboard/firebaseMethods/newId";
import type {
  FirestoreMenuUrlV2Document,
  MenuUrlV2,
  MenuUrlV2WithDocId,
} from "@dashboard/types/menu-url-v2";

/**
 * Converts a Firestore MenuUrlV2 document to a Redux-serializable MenuUrlV2.
 * Converts Timestamps to milliseconds.
 */
export const readMenuUrlV2 = (doc: FirestoreMenuUrlV2Document): MenuUrlV2 => {
  const { createdAt, updatedAt, ...rest } = doc;

  return {
    ...rest,
    createdAtMillis: createdAt.toMillis(),
    updatedAtMillis: updatedAt.toMillis(),
  };
};

export type CreateMenuUrlV2Input = {
  restaurantUuid: string;
  urlSlug: string;
  displayName: string | null;
  menuIdsInDisplayOrder: string[];
  themeId: string | null;
  addressLookupLocationIds?: string[];
  showRestaurantAddress?: boolean;
  hashedStaffAppPIN?: string | null;
};

export type UpdateMenuUrlV2Input = Partial<
  Pick<
    MenuUrlV2,
    | "displayName"
    | "urlSlug"
    | "menuIdsInDisplayOrder"
    | "addressLookupLocationIds"
    | "showRestaurantAddress"
    | "themeId"
    | "hashedStaffAppPIN"
  >
>;

export interface MenuUrlV2Api {
  subscribeToMenuUrlsV2(
    restaurantUuid: string,
    onChange: (menuUrls: MenuUrlV2WithDocId[]) => void,
    onError: (error: Error) => void
  ): Unsubscribe;
  createMenuUrlV2(input: CreateMenuUrlV2Input): Promise<void>;
  updateMenuUrlV2(
    restaurantUuid: string,
    docId: string,
    updates: UpdateMenuUrlV2Input
  ): Promise<void>;
  deleteMenuUrlV2(restaurantUuid: string, docId: string): Promise<void>;
  checkUrlSlugExists(urlSlug: string, excludeDocId?: string): Promise<boolean>;
}

/**
 * An implementation of the MenuUrlV2Api that uses Firestore as the backend.
 * Uses the restaurant subcollection: restaurants-v1/{restaurantUuid}/menu-url-v2/{menuUrlId}
 */
export class FirestoreBackedMenuUrlV2Api implements MenuUrlV2Api {
  private firestoreRefs: FirestoreRefs;

  constructor(params: { firestoreRefs: FirestoreRefs }) {
    this.firestoreRefs = params.firestoreRefs;
  }

  /**
   * Emits all MenuUrlV2 documents for a restaurant, and again on every change.
   */
  public subscribeToMenuUrlsV2(
    restaurantUuid: string,
    onChange: (menuUrls: MenuUrlV2WithDocId[]) => void,
    onError: (error: Error) => void
  ): Unsubscribe {
    return onSnapshot(
      this.firestoreRefs.menuUrlsV2(restaurantUuid),
      (snapshot) =>
        onChange(
          snapshot.docs.map((doc) => ({
            ...readMenuUrlV2(doc.data() as FirestoreMenuUrlV2Document),
            docId: doc.id,
          }))
        ),
      onError
    );
  }

  /**
   * Creates a new MenuUrlV2 document with auto-generated qrCodeId.
   * No separate QrCode document is created - the qrCodeId is stored directly.
   */
  public createMenuUrlV2(input: CreateMenuUrlV2Input): Promise<void> {
    const docId = newId();
    const qrCodeId = newId();
    const now = Timestamp.now();

    const firestoreDoc: FirestoreMenuUrlV2Document = {
      restaurantUuid: input.restaurantUuid,
      qrCodeId,
      urlSlug: input.urlSlug,
      displayName: input.displayName,
      menuIdsInDisplayOrder: input.menuIdsInDisplayOrder,
      themeId: input.themeId,
      addressLookupLocationIds: input.addressLookupLocationIds ?? [],
      showRestaurantAddress: input.showRestaurantAddress ?? false,
      hashedStaffAppPIN: input.hashedStaffAppPIN ?? null,
      createdAt: now,
      updatedAt: now,
    };

    return setDoc(
      this.firestoreRefs.menuUrlV2(input.restaurantUuid, docId),
      firestoreDoc
    );
  }

  /**
   * Updates an existing MenuUrlV2 document.
   */
  public updateMenuUrlV2(
    restaurantUuid: string,
    docId: string,
    updates: UpdateMenuUrlV2Input
  ): Promise<void> {
    return setDoc(
      this.firestoreRefs.menuUrlV2(restaurantUuid, docId),
      { ...updates, updatedAt: Timestamp.now() },
      { merge: true }
    );
  }

  /**
   * Deletes a MenuUrlV2 document.
   */
  public deleteMenuUrlV2(restaurantUuid: string, docId: string): Promise<void> {
    return deleteDoc(this.firestoreRefs.menuUrlV2(restaurantUuid, docId));
  }

  /**
   * Checks if a urlSlug already exists in the menu-urls-v2 collection group.
   * Used for uniqueness validation before create/update.
   */
  public async checkUrlSlugExists(
    urlSlug: string,
    excludeDocId?: string
  ): Promise<boolean> {
    const snapshot = await getDocs(
      query(
        this.firestoreRefs.menuUrlsV2CollectionGroup(),
        where("urlSlug", "==", urlSlug)
      )
    );

    if (snapshot.empty) {
      return false;
    }

    // If we're editing, exclude the current document
    if (excludeDocId) {
      return snapshot.docs.some((doc) => doc.id !== excludeDocId);
    }

    return true;
  }
}
